import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
    Alert,
    Autocomplete,
    Box,
    Chip,
    Container,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    Link,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    OpenInNew as OpenInNewIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import DayRangePicker from '../../components/DayRangePicker';
import ExplorerFilterRow, { ExplorerFilterSlot } from './ExplorerFilterRow';
import { SummarySection } from './SummarySection';
import { ExplorerDetailTableHeaderLabel } from './ExplorerDetailTableHeaderLabel';
import { ExplorerPlatformIcon } from './ExplorerPlatformIcon';
import { clipsExplorerPeriod } from './clipsExplorerPeriod';
import ExplorerExportButton from './ExplorerExportButton';
import { exportAlternatives, SOCIAL_CHANNEL_REPORT_TYPE } from './explorerTabs';
import { useExplorerClipsIa } from './useExplorerClipsIa';
import type { ExportConfig } from '../../export';
import { ExplorerHeader } from './ExplorerHeader';
import { useExplorerDates } from './useExplorerDates';
import { useAuth } from '../../contexts/AuthContext';
import apiService, { type ExplorerSocialMetrics } from '../../services/api';
import { getS3Url, ensureDirectS3Url } from '../../utils/imageUtils';
import { useDebounce } from '../../utils/useDebounce';
import {
    EXPLORER_FILTER_CONTROL_HEIGHT,
    EXPLORER_TABLE_DATA_COL_WIDTH,
    GC_TIME,
    RIGHT_TABLE_SORT_LABEL_SX,
    SOCIAL_PLATFORMS,
    STALE_TIME,
    explorerFilterControlSx,
    explorerPaginationLabel,
    explorerRankingFixedColSx,
    formatExplorerTableDateLine,
    formatExplorerTableTimeLine,
    formatNumber,
    type SocialPlatform,
    type SocialPostSortColumn,
    type SortDirection,
} from './utils';
import { SocialChannelPostRowSkeleton } from './ExplorerLoadingSkeletons';
import { ROUTES_NAMES } from '../../components/routes';
import { normalizeCollectionHashtags } from '../createCollectionPrefill';

const PLATFORM_LABEL_KEY: Record<SocialPlatform, string> = {
    facebook: 'socialFacebook',
    tiktok: 'socialTiktok',
    instagram: 'socialInstagram',
    youtubeShorts: 'socialYoutubeShorts',
};

const CLIPS_TABLE_LINK_COL_WIDTH = 64;

const CLIPS_TABLE_TYPE_COL_WIDTH = 72;

const CLIPS_TABLE_PUBLICATION_COL_WIDTH = 112;

const CLIPS_TABLE_SX = {
    width: '100%',
    tableLayout: 'fixed',
    ...explorerRankingFixedColSx(1, 148),
    ...explorerRankingFixedColSx(2, 108),
    ...explorerRankingFixedColSx(4, CLIPS_TABLE_TYPE_COL_WIDTH),
    ...explorerRankingFixedColSx(5, CLIPS_TABLE_PUBLICATION_COL_WIDTH),
    ...explorerRankingFixedColSx(6, EXPLORER_TABLE_DATA_COL_WIDTH),
    ...explorerRankingFixedColSx(7, EXPLORER_TABLE_DATA_COL_WIDTH),
    '& > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root:nth-of-type(3), & > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:nth-of-type(3)':
        { width: '100%', overflow: 'hidden' },
    '& > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root:nth-of-type(8), & > .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root:nth-of-type(8)':
        {
            width: CLIPS_TABLE_LINK_COL_WIDTH,
            minWidth: CLIPS_TABLE_LINK_COL_WIDTH,
            maxWidth: CLIPS_TABLE_LINK_COL_WIDTH,
            px: 0.5,
        },
} as const;

const EMPTY_METRICS: ExplorerSocialMetrics = {
    posts: 0,
    clips: 0,
    staticPosts: 0,
    views: 0,
    interactions: 0,
    clipInteractions: 0,
    postInteractions: 0,
};

function platformAccountUrl(platform: SocialPlatform, handle: string | null): string | null {
    if (!handle) return null;
    if (platform === 'instagram') return `https://www.instagram.com/${handle}/`;
    if (platform === 'tiktok') return `https://www.tiktok.com/@${handle.replace(/^@/, '')}`;
    if (platform === 'facebook') return `https://www.facebook.com/${handle}`;
    if (handle.startsWith('UC')) return `https://www.youtube.com/channel/${handle}`;
    return `https://www.youtube.com/@${handle.replace(/^@/, '')}`;
}

function displayHandle(platform: SocialPlatform, handle: string): string {
    if (platform === 'facebook') return handle;
    return `@${handle.replace(/^@/, '')}`;
}

const PLATFORMS_WITH_POSTS = new Set<SocialPlatform>(['facebook', 'instagram']);

type PlatformSortColumn =
    | 'platform'
    | 'clips'
    | 'views'
    | 'viewsPerClip'
    | 'clipInteractions'
    | 'staticPosts'
    | 'postInteractions';

function platformSortValue(
    platform: SocialPlatform,
    metrics: ExplorerSocialMetrics,
    column: Exclude<PlatformSortColumn, 'platform'>,
): number {
    const includePosts = platformHasPosts(platform);
    if (column === 'clips') return metrics.clips;
    if (column === 'views') return metrics.views;
    if (column === 'viewsPerClip') return metrics.clips > 0 ? metrics.views / metrics.clips : -1;
    if (column === 'clipInteractions') {
        return includePosts ? metrics.clipInteractions : metrics.interactions;
    }
    if (column === 'staticPosts') return includePosts ? metrics.staticPosts : -1;
    return includePosts ? metrics.postInteractions : -1;
}

function platformHasPosts(platform?: SocialPlatform | 'all'): boolean {
    return platform == null || platform === 'all' || PLATFORMS_WITH_POSTS.has(platform);
}

function summaryCardsFromMetrics(
    metrics: ExplorerSocialMetrics,
    t: (key: string) => string,
    opts: { includeClips?: boolean; includePosts?: boolean } = {},
) {
    const includeClips = opts.includeClips ?? true;
    const includePosts = opts.includePosts ?? true;
    const viewsPerClip = metrics.clips > 0 ? metrics.views / metrics.clips : null;
    return [
        ...(includeClips
            ? [
                  {
                      title: t('clips'),
                      value: formatNumber(metrics.clips),
                      tooltip: t('tooltip.clipCount'),
                  },
              ]
            : []),
        ...(includePosts
            ? [
                  {
                      title: t('staticPosts'),
                      value: formatNumber(metrics.staticPosts),
                      tooltip: t('tooltip.staticPostCount'),
                  },
              ]
            : []),
        ...(includeClips
            ? [
                  {
                      title: t('views'),
                      value: formatNumber(metrics.views),
                      tooltip: t('tooltip.periodViews'),
                  },
                  {
                      title: t('viewsPerClip'),
                      value: viewsPerClip == null ? '' : formatNumber(viewsPerClip),
                      tooltip: t('tooltip.viewsPerClip'),
                  },
                  {
                      title: t('clipInteractions'),
                      value: formatNumber(metrics.clipInteractions),
                      tooltip: t('tooltip.clipInteractions'),
                  },
              ]
            : []),
        ...(includePosts
            ? [
                  {
                      title: t('postInteractions'),
                      value: formatNumber(metrics.postInteractions),
                      tooltip: t('tooltip.postInteractions'),
                  },
              ]
            : []),
    ];
}

function PlatformName({
    platform,
    fontSize = 20,
    nameVariant = 'body2',
}: {
    platform: SocialPlatform;
    fontSize?: number;
    nameVariant?: 'body2' | 'subtitle2';
}) {
    const { t } = useTranslation('explorer');
    const name = t(PLATFORM_LABEL_KEY[platform]);
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <ExplorerPlatformIcon platform={platform} label={name} fontSize={fontSize} />
            <Typography variant={nameVariant} fontWeight={600} noWrap>
                {name}
            </Typography>
        </Stack>
    );
}

function formatMetricOrDash(value: number): string {
    return value > 0 ? formatNumber(value) : '—';
}

const ExplorerSocialChannelDetail: React.FC = () => {
    const { t } = useTranslation('explorer');
    const theme = useTheme();
    const { channelId } = useParams<{ channelId: string }>();
    const navigate = useNavigate();
    const { getDateFilterMinStartDate, getDateFilterMaxEndDate } = useAuth();
    const clipsIa = useExplorerClipsIa();
    const explorerDates = useExplorerDates();
    const currentMinDate = getDateFilterMinStartDate();
    const currentMaxDate = getDateFilterMaxEndDate();
    const { startDate, endDate, minDate: periodMin, maxDate: periodMax } = clipsExplorerPeriod(
        clipsIa,
        explorerDates.startDate,
        explorerDates.endDate,
        currentMinDate,
        currentMaxDate,
    );
    const setRange = explorerDates.setRange;

    const [platformFilter, setPlatformFilter] = useState<SocialPlatform | 'all'>('all');
    const [kindFilter, setKindFilter] = useState<'all' | 'clip' | 'post'>('all');
    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounce(searchInput.trim(), 300);
    const [hashtagFilter, setHashtagFilter] = useState<string[]>([]);
    const [ownershipFilter, setOwnershipFilter] = useState<'all' | 'owned' | 'collab'>('owned');
    const [platformSortColumn, setPlatformSortColumn] = useState<PlatformSortColumn>('views');
    const [platformSortDirection, setPlatformSortDirection] = useState<SortDirection>('desc');
    const [sortColumn, setSortColumn] = useState<SocialPostSortColumn>('publishedAt');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const handlePlatformSort = (column: PlatformSortColumn) => {
        if (platformSortColumn === column) {
            setPlatformSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setPlatformSortColumn(column);
        setPlatformSortDirection(column === 'platform' ? 'asc' : 'desc');
    };

    const handleSort = (column: SocialPostSortColumn) => {
        if (sortColumn === column) {
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortDirection(column === 'title' ? 'asc' : 'desc');
        }
        setPage(0);
    };

    const { data: channel, isLoading: channelLoading, isPlaceholderData: channelPlaceholder, error: channelError } = useQuery({
        queryKey: [
            'explorer-social-channel',
            channelId,
            startDate,
            endDate,
            ownershipFilter,
            platformFilter,
            kindFilter,
            hashtagFilter,
            debouncedSearch,
        ],
        queryFn: ({ signal }) =>
            apiService.getExplorerSocialChannel(channelId!, startDate, endDate, {
                signal,
                ownership: ownershipFilter === 'all' ? undefined : ownershipFilter,
                platform: platformFilter === 'all' ? undefined : platformFilter,
                kind: kindFilter === 'all' ? undefined : kindFilter,
                hashtags: hashtagFilter.length ? hashtagFilter : undefined,
                search: debouncedSearch || undefined,
            }),
        enabled: !!channelId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        placeholderData: keepPreviousData,
    });

    const { data: postsData, isFetching: postsFetching, isPlaceholderData } = useQuery({
        queryKey: [
            'explorer-social-posts',
            channelId,
            startDate,
            endDate,
            platformFilter,
            kindFilter,
            ownershipFilter,
            hashtagFilter,
            debouncedSearch,
            sortColumn,
            sortDirection,
            page,
            rowsPerPage,
        ],
        queryFn: ({ signal }) =>
            apiService.getExplorerSocialChannelPosts(channelId!, startDate, endDate, {
                platform: platformFilter === 'all' ? undefined : platformFilter,
                kind: kindFilter === 'all' ? undefined : kindFilter,
                ownership: ownershipFilter === 'all' ? undefined : ownershipFilter,
                hashtags: hashtagFilter.length ? hashtagFilter : undefined,
                search: debouncedSearch || undefined,
                sortBy: sortColumn,
                sortOrder: sortDirection,
                page: page + 1,
                limit: rowsPerPage,
                signal,
            }),
        enabled: !!channelId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        placeholderData: keepPreviousData,
    });

    const { data: hashtagSuggestions } = useQuery({
        queryKey: [
            'explorer-social-hashtags',
            channelId,
            startDate,
            endDate,
            platformFilter,
            kindFilter,
            ownershipFilter,
        ],
        queryFn: ({ signal }) =>
            apiService.getExplorerSocialChannelHashtags(channelId!, startDate, endDate, {
                platform: platformFilter === 'all' ? undefined : platformFilter,
                kind: kindFilter === 'all' ? undefined : kindFilter,
                ownership: ownershipFilter === 'all' ? undefined : ownershipFilter,
                signal,
            }),
        enabled: !!channelId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        placeholderData: keepPreviousData,
    });

    const hashtagOptions = useMemo(
        () => (hashtagSuggestions ?? []).map((row) => row.tag),
        [hashtagSuggestions],
    );
    const hashtagCounts = useMemo(
        () => new Map((hashtagSuggestions ?? []).map((row) => [row.tag, row.count])),
        [hashtagSuggestions],
    );

    const showPublisherPlatform = useMemo(() => {
        const publishers = channel?.publishers ?? [];
        return publishers.some((row) => publishers.some((other) => other.platform !== row.platform));
    }, [channel?.publishers]);

    const includeClips = kindFilter !== 'post';
    const includePosts = kindFilter !== 'clip' && (kindFilter === 'post' || platformHasPosts(platformFilter));

    const periodCards = useMemo(
        () => summaryCardsFromMetrics(channel?.total ?? EMPTY_METRICS, t, { includeClips, includePosts }),
        [channel, includeClips, includePosts, t],
    );

    const exportConfig = useMemo<ExportConfig | undefined>(() => {
        if (!channelId) return undefined;
        return {
            reportType: SOCIAL_CHANNEL_REPORT_TYPE,
            params: {
                startDate,
                endDate,
                channelId,
                ...(ownershipFilter !== 'all' ? { ownership: ownershipFilter } : {}),
                ...(platformFilter !== 'all' ? { platform: platformFilter } : {}),
                ...(kindFilter !== 'all' ? { kind: kindFilter } : {}),
                ...(hashtagFilter.length ? { hashtags: hashtagFilter.join(',') } : {}),
                ...(debouncedSearch ? { search: debouncedSearch } : {}),
            },
            contextLabels: [
                ...(channel?.name ? [{ label: t('channel'), value: channel.name }] : []),
                ...(debouncedSearch ? [{ label: t('search'), value: debouncedSearch }] : []),
                ...(hashtagFilter.length
                    ? [{ label: t('hashtags'), value: hashtagFilter.map((tag) => `#${tag}`).join(', ') }]
                    : []),
            ],
            alternatives: exportAlternatives([SOCIAL_CHANNEL_REPORT_TYPE]),
        };
    }, [channelId, channel?.name, startDate, endDate, ownershipFilter, platformFilter, kindFilter, hashtagFilter, debouncedSearch, t]);

    const openClipsFor = (platform: SocialPlatform) => {
        setPlatformFilter((current) => (current === platform ? 'all' : platform));
        setPage(0);
    };

    const connectedAccounts = useMemo(() => {
        if (!channel) return [];
        return SOCIAL_PLATFORMS.flatMap((platform) => {
            if (!channel.connected[platform]) return [];
            const handle = channel.handles[platform];
            if (!handle) return [];
            return [
                {
                    platform,
                    handle,
                    href: platformAccountUrl(platform, handle),
                    followers: channel.followers[platform],
                },
            ];
        });
    }, [channel]);

    const showHeaderSkeleton = channelLoading && !channel;
    const showPostsSkeleton = postsFetching && !postsData;
    const showPostsRefetching = postsFetching && isPlaceholderData;

    const platformSelect = (
        <FormControl size="small" sx={explorerFilterControlSx()}>
            <InputLabel>{t('socialPlatform')}</InputLabel>
            <Select
                value={platformFilter}
                label={t('socialPlatform')}
                renderValue={(value) =>
                    value === 'all' ? (
                        t('socialAllPlatforms')
                    ) : (
                        <PlatformName platform={value} fontSize={18} />
                    )
                }
                onChange={(e) => {
                    setPlatformFilter(e.target.value as SocialPlatform | 'all');
                    setPage(0);
                }}
            >
                <MenuItem value="all">{t('socialAllPlatforms')}</MenuItem>
                {SOCIAL_PLATFORMS.map((platform) => (
                    <MenuItem key={platform} value={platform}>
                        <PlatformName platform={platform} fontSize={18} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const kindSelect = (
        <FormControl size="small" sx={explorerFilterControlSx()}>
            <InputLabel>{t('contentType')}</InputLabel>
            <Select
                value={kindFilter}
                label={t('contentType')}
                onChange={(e) => {
                    const next = e.target.value as 'all' | 'clip' | 'post';
                    setKindFilter(next);
                    setPage(0);
                    if (
                        next === 'post' &&
                        (platformSortColumn === 'clips' ||
                            platformSortColumn === 'views' ||
                            platformSortColumn === 'viewsPerClip' ||
                            platformSortColumn === 'clipInteractions')
                    ) {
                        setPlatformSortColumn('staticPosts');
                        setPlatformSortDirection('desc');
                    }
                    if (
                        next === 'clip' &&
                        (platformSortColumn === 'staticPosts' ||
                            platformSortColumn === 'postInteractions')
                    ) {
                        setPlatformSortColumn('views');
                        setPlatformSortDirection('desc');
                    }
                }}
            >
                <MenuItem value="all">{t('contentTypeAll')}</MenuItem>
                <MenuItem value="clip">{t('clipKind')}</MenuItem>
                <MenuItem value="post">{t('postKind')}</MenuItem>
            </Select>
        </FormControl>
    );

    const searchField = (
        <TextField
            size="small"
            label={t('search')}
            placeholder={t('searchByClip')}
            value={searchInput}
            onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(0);
            }}
            sx={{
                ...explorerFilterControlSx(200),
                minWidth: { xs: '100%', sm: 160 },
                flex: { xs: '1 1 100%', sm: '1 1 200px' },
            }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                ),
                endAdornment: searchInput ? (
                    <InputAdornment position="end">
                        <IconButton
                            size="small"
                            aria-label={t('clearSearch')}
                            edge="end"
                            onClick={() => {
                                setSearchInput('');
                                setPage(0);
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </InputAdornment>
                ) : undefined,
            }}
        />
    );

    const hashtagSelect = (
        <Autocomplete
            multiple
            freeSolo
            autoSelect
            size="small"
            options={hashtagOptions}
            value={hashtagFilter}
            onChange={(_, next) => {
                setHashtagFilter(normalizeCollectionHashtags(next));
                setPage(0);
            }}
            filterSelectedOptions
            getOptionLabel={(option) => `#${String(option).replace(/^#+/, '')}`}
            renderOption={(props, option) => {
                const count = hashtagCounts.get(option);
                return (
                    <li {...props} key={option}>
                        #{option}
                        {count != null ? ` · ${formatNumber(count)}` : ''}
                    </li>
                );
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={option}
                        size="small"
                        label={`#${option}`}
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={t('hashtags')}
                    placeholder={hashtagFilter.length ? undefined : t('searchByHashtag')}
                />
            )}
            sx={{
                width: '100%',
                minWidth: 0,
                flex: '1 1 180px',
                '& .MuiInputBase-root': { minHeight: EXPLORER_FILTER_CONTROL_HEIGHT },
            }}
        />
    );

    const publicationSelect = (
        <FormControl size="small" sx={explorerFilterControlSx()}>
            <InputLabel>{t('publication')}</InputLabel>
            <Select
                value={ownershipFilter}
                label={t('publication')}
                onChange={(e) => {
                    setOwnershipFilter(e.target.value as 'all' | 'owned' | 'collab');
                    setPage(0);
                }}
            >
                <MenuItem value="all">{t('publicationAll')}</MenuItem>
                <MenuItem value="owned">{t('publicationOwned')}</MenuItem>
                <MenuItem value="collab">{t('publicationCollab')}</MenuItem>
            </Select>
        </FormControl>
    );

    const tableCount = postsData?.pagination.total ?? channel?.total.posts ?? 0;

    const connectedPlatforms = SOCIAL_PLATFORMS.filter(
        (platform) => channel?.connected[platform],
    );

    const sortedPlatforms = useMemo(() => {
        const source = kindFilter === 'post'
            ? SOCIAL_PLATFORMS.filter((platform) => PLATFORMS_WITH_POSTS.has(platform))
            : SOCIAL_PLATFORMS;
        const platforms = showHeaderSkeleton
            ? source
            : connectedPlatforms.filter((platform) => source.includes(platform));
        const direction = platformSortDirection === 'asc' ? 1 : -1;
        return [...platforms].sort((a, b) => {
            if (platformSortColumn === 'platform') {
                return (
                    t(PLATFORM_LABEL_KEY[a]).localeCompare(t(PLATFORM_LABEL_KEY[b])) * direction
                );
            }
            const left = platformSortValue(a, channel?.[a] ?? EMPTY_METRICS, platformSortColumn);
            const right = platformSortValue(b, channel?.[b] ?? EMPTY_METRICS, platformSortColumn);
            if (left === right) return 0;
            return (left < right ? -1 : 1) * direction;
        });
    }, [
        channel,
        connectedPlatforms,
        kindFilter,
        platformSortColumn,
        platformSortDirection,
        showHeaderSkeleton,
        t,
    ]);

    const platformSortHeader = (
        column: PlatformSortColumn,
        label: string,
        tooltip: string,
        align: 'left' | 'right' = 'right',
    ) => {
        const active = platformSortColumn === column;
        return (
            <TableCell
                align={align}
                sortDirection={active ? platformSortDirection : false}
            >
                <TableSortLabel
                    active={active}
                    direction={
                        active
                            ? platformSortDirection
                            : column === 'platform'
                              ? 'asc'
                              : 'desc'
                    }
                    onClick={() => handlePlatformSort(column)}
                    sx={align === 'right' ? RIGHT_TABLE_SORT_LABEL_SX : undefined}
                >
                    <ExplorerDetailTableHeaderLabel
                        label={label}
                        tooltip={tooltip}
                        align={align}
                    />
                </TableSortLabel>
            </TableCell>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ pb: 4 }}>
            <ExplorerHeader
                loading={showHeaderSkeleton}
                onBack={() => navigate(ROUTES_NAMES.EXPLORER_SOCIAL_LIST)}
                image={{
                    name: channel?.name ?? '',
                    src: channel?.imageS3Key
                        ? ensureDirectS3Url(getS3Url(channel.imageS3Key))
                        : undefined,
                }}
                title={channel?.name ?? ''}
            >
                {showHeaderSkeleton || connectedAccounts.length > 0 ? (
                    <Stack
                        direction="row"
                        spacing={1.5}
                        flexWrap="wrap"
                        useFlexGap
                        justifyContent="flex-end"
                    >
                        {connectedAccounts.map((account) => {
                            const handle = displayHandle(account.platform, account.handle);
                            const followers =
                                account.followers != null
                                    ? ` · ${formatNumber(account.followers)}`
                                    : '';
                            return (
                                <Stack
                                    key={account.platform}
                                    direction="row"
                                    spacing={0.75}
                                    alignItems="center"
                                >
                                    <ExplorerPlatformIcon
                                        platform={account.platform}
                                        label={t(PLATFORM_LABEL_KEY[account.platform])}
                                        fontSize={18}
                                    />
                                    {account.href ? (
                                        <Link
                                            href={account.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            color="text.secondary"
                                            variant="body2"
                                        >
                                            {handle}
                                            {followers}
                                        </Link>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {handle}
                                            {followers}
                                        </Typography>
                                    )}
                                </Stack>
                            );
                        })}
                    </Stack>
                ) : null}
            </ExplorerHeader>

            {channelError ? (
                <Alert severity="error">{t('loadSocialChannelError')}</Alert>
            ) : (
                <>
                    <ExplorerFilterRow>
                        <ExplorerFilterSlot slot="search">
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.5}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                sx={{ width: '100%', minWidth: 0 }}
                            >
                                {searchField}
                                {hashtagSelect}
                            </Stack>
                        </ExplorerFilterSlot>
                        <ExplorerFilterSlot slot="groupBy">{platformSelect}</ExplorerFilterSlot>
                        <ExplorerFilterSlot slot="channelType">{kindSelect}</ExplorerFilterSlot>
                        <ExplorerFilterSlot slot="category">{publicationSelect}</ExplorerFilterSlot>
                        <ExplorerFilterSlot slot="dates">
                            <DayRangePicker
                                startDate={startDate}
                                endDate={endDate}
                                onChange={setRange}
                                minDate={periodMin ?? currentMinDate}
                                maxDate={periodMax ?? currentMaxDate}
                            />
                        </ExplorerFilterSlot>
                        <ExplorerFilterSlot slot="export">
                            <ExplorerExportButton
                                config={exportConfig}
                                tooltip={t('tooltip.clipChannelExport')}
                            />
                        </ExplorerFilterSlot>
                    </ExplorerFilterRow>
                    <Box sx={{ mb: 4 }}>
                        <SummarySection
                            title={t('periodSummary')}
                            isLoading={(channelLoading && !channel) || channelPlaceholder}
                            valueColor={theme.palette.text.primary}
                            cards={periodCards}
                        />
                        {ownershipFilter === 'collab' && (channel?.publishers?.length ?? 0) > 0 ? (
                            <Box
                                sx={{
                                    mt: 3,
                                    ...(channelPlaceholder
                                        ? { opacity: 0.55, pointerEvents: 'none' }
                                        : {}),
                                }}
                            >
                                <Typography variant="h2" color="text.primary" sx={{ mb: 0.5 }}>
                                    {t('collabPublishersTitle')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t('collabPublishersBody', { name: channel?.name ?? '' })}
                                </Typography>
                                <TableContainer component={Paper} sx={{ mb: 3 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{t('collabPublisherAccount')}</TableCell>
                                                {showPublisherPlatform ? (
                                                    <TableCell>{t('socialPlatform')}</TableCell>
                                                ) : null}
                                                {includeClips ? (
                                                    <TableCell align="right">{t('clips')}</TableCell>
                                                ) : null}
                                                {includePosts ? (
                                                    <TableCell align="right">{t('staticPosts')}</TableCell>
                                                ) : null}
                                                {includeClips ? (
                                                    <TableCell align="right">{t('views')}</TableCell>
                                                ) : null}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(channel?.publishers ?? []).map((row) => (
                                                <TableRow key={`${row.platform}-${row.publisher}`}>
                                                    <TableCell>{row.publisher}</TableCell>
                                                    {showPublisherPlatform ? (
                                                        <TableCell>
                                                            <PlatformName platform={row.platform} fontSize={16} />
                                                        </TableCell>
                                                    ) : null}
                                                    {includeClips ? (
                                                        <TableCell align="right">
                                                            {formatNumber(row.clips)}
                                                        </TableCell>
                                                    ) : null}
                                                    {includePosts ? (
                                                        <TableCell align="right">
                                                            {formatNumber(row.posts)}
                                                        </TableCell>
                                                    ) : null}
                                                    {includeClips ? (
                                                        <TableCell align="right">
                                                            {formatNumber(row.views)}
                                                        </TableCell>
                                                    ) : null}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        ) : null}
                        {platformFilter === 'all' ? (
                            <>
                                <Typography variant="h2" color="text.primary" sx={{ mb: 2 }}>
                                    {t('byPlatform')}
                                </Typography>
                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        mb: 0,
                                        ...(channelPlaceholder
                                            ? { opacity: 0.55, pointerEvents: 'none' }
                                            : {}),
                                    }}
                                >
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                {platformSortHeader(
                                                    'platform',
                                                    t('socialPlatform'),
                                                    t('tooltip.clipPlatform'),
                                                    'left',
                                                )}
                                                {includeClips
                                                    ? platformSortHeader(
                                                          'clips',
                                                          t('clips'),
                                                          t('tooltip.clipCount'),
                                                      )
                                                    : null}
                                                {includeClips
                                                    ? platformSortHeader(
                                                          'views',
                                                          t('views'),
                                                          t('tooltip.periodViews'),
                                                      )
                                                    : null}
                                                {includeClips
                                                    ? platformSortHeader(
                                                          'viewsPerClip',
                                                          t('viewsPerClip'),
                                                          t('tooltip.viewsPerClip'),
                                                      )
                                                    : null}
                                                {includeClips
                                                    ? platformSortHeader(
                                                          'clipInteractions',
                                                          t('clipInteractions'),
                                                          t('tooltip.clipInteractions'),
                                                      )
                                                    : null}
                                                {includePosts
                                                    ? platformSortHeader(
                                                          'staticPosts',
                                                          t('staticPosts'),
                                                          t('tooltip.staticPostCount'),
                                                      )
                                                    : null}
                                                {includePosts
                                                    ? platformSortHeader(
                                                          'postInteractions',
                                                          t('postInteractions'),
                                                          t('tooltip.postInteractions'),
                                                      )
                                                    : null}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {sortedPlatforms.map((platform) => {
                                                const metrics = channel?.[platform] ?? EMPTY_METRICS;
                                                const platformShowsPosts = platformHasPosts(platform);
                                                return (
                                                    <TableRow
                                                        key={platform}
                                                        hover={!showHeaderSkeleton}
                                                        onClick={
                                                            showHeaderSkeleton
                                                                ? undefined
                                                                : () => openClipsFor(platform)
                                                        }
                                                        sx={{
                                                            cursor: showHeaderSkeleton
                                                                ? 'default'
                                                                : 'pointer',
                                                        }}
                                                    >
                                                        <TableCell>
                                                            {showHeaderSkeleton ? (
                                                                <Skeleton width={120} />
                                                            ) : (
                                                                <PlatformName
                                                                    platform={platform}
                                                                    fontSize={18}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        {includeClips ? (
                                                            <TableCell align="right">
                                                                {showHeaderSkeleton ? (
                                                                    <Skeleton
                                                                        width={40}
                                                                        sx={{ ml: 'auto' }}
                                                                    />
                                                                ) : (
                                                                    formatNumber(metrics.clips)
                                                                )}
                                                            </TableCell>
                                                        ) : null}
                                                        {includeClips ? (
                                                            <TableCell align="right">
                                                                {showHeaderSkeleton ? (
                                                                    <Skeleton
                                                                        width={48}
                                                                        sx={{ ml: 'auto' }}
                                                                    />
                                                                ) : (
                                                                    formatNumber(metrics.views)
                                                                )}
                                                            </TableCell>
                                                        ) : null}
                                                        {includeClips ? (
                                                            <TableCell align="right">
                                                                {showHeaderSkeleton ? (
                                                                    <Skeleton
                                                                        width={48}
                                                                        sx={{ ml: 'auto' }}
                                                                    />
                                                                ) : metrics.clips > 0 ? (
                                                                    formatNumber(
                                                                        metrics.views / metrics.clips,
                                                                    )
                                                                ) : (
                                                                    '—'
                                                                )}
                                                            </TableCell>
                                                        ) : null}
                                                        {includeClips ? (
                                                            <TableCell align="right">
                                                                {showHeaderSkeleton ? (
                                                                    <Skeleton
                                                                        width={48}
                                                                        sx={{ ml: 'auto' }}
                                                                    />
                                                                ) : (
                                                                    formatNumber(
                                                                        platformShowsPosts
                                                                            ? metrics.clipInteractions
                                                                            : metrics.interactions,
                                                                    )
                                                                )}
                                                            </TableCell>
                                                        ) : null}
                                                        {includePosts ? (
                                                            <TableCell align="right">
                                                                {showHeaderSkeleton ? (
                                                                    <Skeleton
                                                                        width={40}
                                                                        sx={{ ml: 'auto' }}
                                                                    />
                                                                ) : platformShowsPosts ? (
                                                                    formatNumber(metrics.staticPosts)
                                                                ) : (
                                                                    '—'
                                                                )}
                                                            </TableCell>
                                                        ) : null}
                                                        {includePosts ? (
                                                            <TableCell align="right">
                                                                {showHeaderSkeleton ? (
                                                                    <Skeleton
                                                                        width={48}
                                                                        sx={{ ml: 'auto' }}
                                                                    />
                                                                ) : platformShowsPosts ? (
                                                                    formatNumber(
                                                                        metrics.postInteractions,
                                                                    )
                                                                ) : (
                                                                    '—'
                                                                )}
                                                            </TableCell>
                                                        ) : null}
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        ) : null}
                    </Box>

                    <Typography variant="h2" color="text.primary" sx={{ mb: 2 }}>
                        {includeClips && includePosts
                            ? t('recentClipsAndPosts', {
                                  clips: channel?.total.clips ?? 0,
                                  posts: channel?.total.staticPosts ?? 0,
                              })
                            : includeClips
                              ? t('recentClips', { count: channel?.total.clips ?? 0 })
                              : t('recentPosts', { count: channel?.total.staticPosts ?? 0 })}
                    </Typography>

                    <TableContainer
                        component={Paper}
                        sx={{
                            mb: 2,
                            position: 'relative',
                            width: '100%',
                            overflowX: 'hidden',
                            ...(showPostsRefetching ? { opacity: 0.55, pointerEvents: 'none' } : {}),
                        }}
                    >
                        {showPostsRefetching && (
                            <LinearProgress
                                sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }}
                            />
                        )}
                        <Table aria-busy={showPostsSkeleton || showPostsRefetching} sx={CLIPS_TABLE_SX}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <ExplorerDetailTableHeaderLabel
                                            label={t('socialPlatform')}
                                            tooltip={t('tooltip.clipPlatform')}
                                        />
                                    </TableCell>
                                    <TableCell
                                        sortDirection={sortColumn === 'publishedAt' ? sortDirection : false}
                                    >
                                        <TableSortLabel
                                            active={sortColumn === 'publishedAt'}
                                            direction={
                                                sortColumn === 'publishedAt' ? sortDirection : 'desc'
                                            }
                                            onClick={() => handleSort('publishedAt')}
                                        >
                                            <ExplorerDetailTableHeaderLabel
                                                label={t('date')}
                                                tooltip={t('tooltip.clipPublishedAt')}
                                            />
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        sortDirection={sortColumn === 'title' ? sortDirection : false}
                                    >
                                        <TableSortLabel
                                            active={sortColumn === 'title'}
                                            direction={sortColumn === 'title' ? sortDirection : 'asc'}
                                            onClick={() => handleSort('title')}
                                        >
                                            <ExplorerDetailTableHeaderLabel
                                                label={t('title')}
                                                tooltip={t('tooltip.clipTitle')}
                                            />
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <ExplorerDetailTableHeaderLabel
                                            label={t('contentType')}
                                            tooltip={t('tooltip.contentType')}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <ExplorerDetailTableHeaderLabel
                                            label={t('publication')}
                                            tooltip={t('tooltip.publication')}
                                        />
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sortDirection={sortColumn === 'views' ? sortDirection : false}
                                    >
                                        <TableSortLabel
                                            active={sortColumn === 'views'}
                                            direction={sortColumn === 'views' ? sortDirection : 'desc'}
                                            onClick={() => handleSort('views')}
                                            sx={RIGHT_TABLE_SORT_LABEL_SX}
                                        >
                                            <ExplorerDetailTableHeaderLabel
                                                label={t('views')}
                                                tooltip={t('tooltip.clipViews')}
                                                align="right"
                                            />
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sortDirection={
                                            sortColumn === 'interactions' ? sortDirection : false
                                        }
                                    >
                                        <TableSortLabel
                                            active={sortColumn === 'interactions'}
                                            direction={
                                                sortColumn === 'interactions' ? sortDirection : 'desc'
                                            }
                                            onClick={() => handleSort('interactions')}
                                            sx={RIGHT_TABLE_SORT_LABEL_SX}
                                        >
                                            <ExplorerDetailTableHeaderLabel
                                                label={t('interactions')}
                                                tooltip={t('tooltip.interactionsSum')}
                                                align="right"
                                            />
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {showPostsSkeleton
                                    ? Array.from({ length: 5 }).map((_, index) => (
                                          <SocialChannelPostRowSkeleton key={`post-skeleton-${index}`} />
                                      ))
                                    : postsData?.posts.length === 0
                                      ? (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center">
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ py: 4 }}
                                                    >
                                                        {t('noSocialPosts')}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                      : postsData?.posts.map((post) => (
                                            <TableRow
                                                key={post._id}
                                                hover
                                                onClick={() => {
                                                    if (!post.url) return;
                                                    window.open(post.url, '_blank', 'noopener,noreferrer');
                                                }}
                                                sx={{ cursor: post.url ? 'pointer' : 'default' }}
                                            >
                                                <TableCell>
                                                    <PlatformName
                                                        platform={post.platform}
                                                        fontSize={18}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.25}>
                                                        <Typography variant="body2" color="text.primary">
                                                            {formatExplorerTableDateLine(post.publishedAt)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {formatExplorerTableTimeLine(post.publishedAt)}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight={600}
                                                        noWrap
                                                        title={post.title || undefined}
                                                        sx={{
                                                            display: 'block',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {post.title || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {platformHasPosts(post.platform)
                                                            ? post.kind === 'post'
                                                                ? t('postKind')
                                                                : t('clipKind')
                                                            : t('clipKind')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {post.owned
                                                            ? t('publicationOwned')
                                                            : t('publicationCollab')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {post.kind === 'clip'
                                                        ? formatMetricOrDash(post.views)
                                                        : '—'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNumber(post.interactions)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {post.url ? (
                                                        <IconButton
                                                            size="small"
                                                            component="a"
                                                            href={post.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            aria-label={t('openClip')}
                                                            onClick={(event) => event.stopPropagation()}
                                                        >
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={tableCount}
                        page={page}
                        onPageChange={(_event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        labelRowsPerPage={t('rowsPerPage')}
                        labelDisplayedRows={({ from, to, count }) =>
                            explorerPaginationLabel(from, to, count)
                        }
                    />
                </>
            )}
        </Container>
    );
};

export default ExplorerSocialChannelDetail;
